import React from 'react';
import { FileDown, Building2, Calendar, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export interface ProposalSection {
  heading: string;
  content: string;
}

export interface ProposalSpec {
  title?: string;
  company?: string;
  client?: string;
  date?: string;
  proposalNumber?: string;
  sections: ProposalSection[];
}

interface InlineChatProposalProps {
  spec: ProposalSpec;
}

/** Escape HTML special characters to prevent XSS in document.write */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Strip citation markers like [1], [2] from proposal text */
function stripCitations(text: string): string {
  return text.replace(/\s*\[\d+\]\.?/g, '').trim();
}

/** Parse markdown pipe tables into structured data */
function parseMarkdownTable(text: string): { before: string; headers: string[]; rows: string[][]; after: string } | null {
  const lines = text.split('\n');
  let tableStart = -1;
  let tableEnd = -1;

  // Find the first line that starts with |
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (tableStart === -1) tableStart = i;
      tableEnd = i;
    } else if (tableStart !== -1 && tableEnd !== -1) {
      break; // End of table block
    }
  }

  if (tableStart === -1 || tableEnd - tableStart < 1) return null;

  const tableLines = lines.slice(tableStart, tableEnd + 1);
  const parseCells = (row: string) => row.split('|').slice(1, -1).map(c => c.trim());
  const isSeparator = (row: string) => parseCells(row).every(c => /^[:\-]+$/.test(c));

  const headers = parseCells(tableLines[0]);
  const dataStart = tableLines.length > 1 && isSeparator(tableLines[1]) ? 2 : 1;
  const rows = tableLines.slice(dataStart).filter(r => !isSeparator(r)).map(parseCells);

  if (headers.length === 0 || rows.length === 0) return null;

  return {
    before: lines.slice(0, tableStart).join('\n').trim(),
    headers,
    rows,
    after: lines.slice(tableEnd + 1).join('\n').trim(),
  };
}

/**
 * Renders section content - handles markdown tables and plain text.
 * Strips citation markers from all content.
 */
function SectionContent({ content }: { content: string }) {
  const clean = stripCitations(content);
  const table = parseMarkdownTable(clean);

  if (table) {
    return (
      <>
        {table.before && (
          <p className="text-sm text-f-dark/80 leading-relaxed mb-2">{table.before}</p>
        )}
        <div className="overflow-x-auto rounded-lg border border-gray-200 my-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-cream/60">
                {table.headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold text-f-dark border-b border-gray-200 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-brand-cream/30'}>
                  {table.headers.map((_, ci) => (
                    <td key={ci} className="px-3 py-2 text-f-dark/80 border-b border-gray-100">
                      {row[ci] || ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {table.after && (
          <p className="text-sm text-f-dark/80 leading-relaxed mt-2">{table.after}</p>
        )}
      </>
    );
  }

  return (
    <p className="text-sm text-f-dark/80 whitespace-pre-wrap leading-relaxed">{clean}</p>
  );
}

/**
 * Renders a professional proposal card inline within the chat.
 * Uses site brand colors (f-dark sidebar theme).
 * Includes a "Download as PDF" button that opens a print-friendly window.
 */
export const InlineChatProposal: React.FC<InlineChatProposalProps> = ({ spec }) => {
  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Popup blocked - please allow popups for this site to download PDFs');
      return;
    }

    const proposalTitle = escapeHtml(spec.title || 'Landscape Service Proposal');

    // Build section HTML - strip citations and render tables as HTML
    const sectionsHtml = spec.sections.map((s, i) => {
      const cleanContent = stripCitations(s.content);
      const table = parseMarkdownTable(cleanContent);
      let contentHtml: string;

      if (table) {
        const tableHtml = `
          ${table.before ? `<p>${escapeHtml(table.before)}</p>` : ''}
          <table>
            <thead><tr>${table.headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
            <tbody>${table.rows.map(row =>
              `<tr>${table.headers.map((_, ci) => `<td>${escapeHtml(row[ci] || '')}</td>`).join('')}</tr>`
            ).join('')}</tbody>
          </table>
          ${table.after ? `<p>${escapeHtml(table.after)}</p>` : ''}
        `;
        contentHtml = tableHtml;
      } else {
        contentHtml = `<div class="section-content">${escapeHtml(cleanContent)}</div>`;
      }

      return `
        <div class="section">
          <h2>${i + 1}. ${escapeHtml(s.heading)}</h2>
          ${contentHtml}
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${proposalTitle}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #1a1a1a;
            line-height: 1.6;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .proposal-header {
            text-align: center;
            border-bottom: 3px solid #0B1F3A;
            padding-bottom: 24px;
            margin-bottom: 32px;
          }
          .proposal-header h1 {
            font-size: 28px;
            color: #0B1F3A;
            margin-bottom: 16px;
          }
          .meta-row { display: flex; justify-content: center; gap: 32px; color: #555; font-size: 14px; }
          .section { margin-bottom: 24px; }
          .section h2 {
            font-size: 18px;
            color: #0B1F3A;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 8px;
            margin-bottom: 12px;
          }
          .section-content { font-size: 14px; white-space: pre-wrap; }
          .section p { font-size: 14px; margin: 4px 0; }
          table { width: 100%; border-collapse: collapse; margin: 12px 0; }
          th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; font-size: 13px; }
          th { background: #EDF1F7; font-weight: 600; color: #0B1F3A; }
          tr:nth-child(even) { background: #f9f8f7; }
          @media print {
            body { padding: 20px; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="proposal-header">
          <h1>${proposalTitle}</h1>
          <div class="meta-row">
            ${spec.company ? `<span><strong>From:</strong> ${escapeHtml(spec.company)}</span>` : ''}
            ${spec.client ? `<span><strong>To:</strong> ${escapeHtml(spec.client)}</span>` : ''}
            ${spec.date ? `<span><strong>Date:</strong> ${escapeHtml(spec.date)}</span>` : ''}
            ${spec.proposalNumber ? `<span><strong>#:</strong> ${escapeHtml(spec.proposalNumber)}</span>` : ''}
          </div>
        </div>
        ${sectionsHtml}
        <script>
          window.onload = function() { window.print(); };
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="my-3 rounded-xl bg-brand-cream/40 border border-brand-cream overflow-hidden">
      {/* Header bar - brand f-dark */}
      <div className="bg-f-dark px-5 py-3 flex items-center justify-between">
        <div className="min-w-0 flex-1 mr-3">
          <h3 className="text-base font-semibold text-white">
            {spec.title || 'Landscape Service Proposal'}
          </h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-white/60">
            {spec.company && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3 h-3 flex-shrink-0" /> {spec.company}
              </span>
            )}
            {spec.client && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3 h-3 flex-shrink-0" /> {spec.client}
              </span>
            )}
            {spec.date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3 flex-shrink-0" /> {spec.date}
              </span>
            )}
            {spec.proposalNumber && (
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3 flex-shrink-0" /> {spec.proposalNumber}
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleDownloadPDF}
          className="bg-white text-f-dark hover:bg-brand-cream text-xs gap-1.5 flex-shrink-0"
        >
          <FileDown className="w-3.5 h-3.5" />
          Download PDF
        </Button>
      </div>

      {/* Sections - light background matching site */}
      <div className="px-5 py-4 space-y-4 bg-white">
        {spec.sections.map((section, i) => (
          <div key={i}>
            <h4 className="text-sm font-semibold text-f-dark mb-1.5">
              {i + 1}. {section.heading}
            </h4>
            <SectionContent content={section.content} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default InlineChatProposal;
